import { ColumnSizingState, flexRender, Row, Table } from '@tanstack/react-table'
import classNames from 'classnames'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'reactstrap'
import SkeletonTable from '../../Common/Skeleton/SkeletonTable'
import SortingIcon from './SortingIcon'

export interface RenderRowProps<TData> {
    row: Row<TData>
    children: (rowProps?: Record<string, unknown>) => ReactNode
}

interface DataTableProps<TData> {
    table: Table<TData>
    isLoading?: boolean
    useSkeleton?: boolean
    noResultMessage?: string
    theadClass?: string
    tableFooter?: ReactNode
    autoLayout?: boolean
    updateColumnSizing?: (sizing: ColumnSizingState) => void
    renderRow?: (props: RenderRowProps<TData>) => ReactNode
}

const DataTable = <TData,>(props: DataTableProps<TData>) => {
    const { table, isLoading, useSkeleton, noResultMessage, theadClass, tableFooter, autoLayout, updateColumnSizing, renderRow } = props

    const { t } = useTranslation()
    const rows = table.getRowModel().rows
    const emptyMessage = noResultMessage ?? t('app.common.noResult')
    const colSpan = table.getAllLeafColumns().length

    const emptyRow = (
        <tr>
            <td colSpan={colSpan} className="text-center">
                {emptyMessage}
            </td>
        </tr>
    )

    const tableRows = rows.map((row) => {
        const cells = (rowProps: Record<string, unknown> = {}) => (
            <tr key={row.id} {...rowProps}>
                {row.getVisibleCells().map((cell) => (
                    <td
                        key={cell.id}
                        className={cell.column.columnDef.meta?.tdClass}
                        style={{ width: cell.column.getSize() }}
                        onClick={cell.column.columnDef.meta?.stopPropagation ? (e) => e.stopPropagation() : undefined}
                    >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                ))}
            </tr>
        )

        return renderRow ? renderRow({ row, children: cells }) : cells()
    })

    const tableHeader = (
        <thead className={theadClass}>
            {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                        const canSort = header.column.getCanSort()
                        const sortDirection = header.column.getIsSorted()
                        const canResize = header.column.getCanResize()

                        return (
                            <th
                                key={header.id}
                                colSpan={header.colSpan}
                                style={{ width: header.getSize(), position: 'relative' }}
                                data-testid={header.id}
                            >
                                {header.isPlaceholder ? null : (
                                    <div
                                        className={classNames(
                                            'd-flex align-items-center overflow-hidden',
                                            header.column.columnDef.meta?.thDivClass || 'justify-content-between'
                                        )}
                                    >
                                        <span className="text-truncate">{flexRender(header.column.columnDef.header, header.getContext())}</span>
                                        {canSort && (
                                            <SortingIcon
                                                sort={sortDirection || undefined}
                                                onSort={(desc: boolean) => header.column.toggleSorting(desc)}
                                            />
                                        )}
                                    </div>
                                )}
                                {canResize && updateColumnSizing && (
                                    <div
                                        className="column-resizer"
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                    />
                                )}
                            </th>
                        )
                    })}
                </tr>
            ))}
        </thead>
    )

    const tableContent = () => {
        if (isLoading) {
            if (useSkeleton) return <SkeletonTable />

            return (
                <tbody>
                    <tr>
                        <td colSpan={colSpan} className="text-center py-5">
                            <Spinner color="primary" />
                        </td>
                    </tr>
                </tbody>
            )
        }

        if (rows.length === 0) {
            return (
                <>
                    {tableHeader}
                    <tbody>{emptyRow}</tbody>
                </>
            )
        }

        return (
            <>
                {tableHeader}
                <tbody>{tableRows}</tbody>
            </>
        )
    }

    return (
        <div className="table-responsive">
            <table
                className="table table-bordered table-striped table-responsive-custom"
                style={
                    isLoading
                        ? { tableLayout: 'fixed', width: '100%' }
                        : { tableLayout: autoLayout ? 'auto' : 'fixed', width: autoLayout ? '100%' : table.getTotalSize(), minWidth: '100%' }
                }
            >
                {tableContent()}
                {tableFooter && <tfoot>{tableFooter}</tfoot>}
            </table>
        </div>
    )
}

export default DataTable
